import {
  Chart as ChartJS,
  type ChartDataset,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  type TooltipItem,
} from 'chart.js'
import { useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import type { FeatureTimelineData, ReleaseBandsData } from '../../lib/types'
import {
  createReleaseBandsPlugin,
  ReleaseBandsLegend,
} from './release-bands'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

interface FeatureTimelineChartProps {
  data: FeatureTimelineData
  releaseBands?: ReleaseBandsData
  granularity?: 'day' | 'week'
  launchDate?: string
  maxFeatures?: number
}

const COLORS = [
  'hsl(222.2, 47.4%, 11.2%)',
  'hsl(215.4, 16.3%, 46.9%)',
  'hsl(210, 40%, 60%)',
  'hsl(200, 50%, 50%)',
  'hsl(180, 40%, 45%)',
  'hsl(160, 50%, 40%)',
  'hsl(140, 45%, 45%)',
  'hsl(120, 40%, 50%)',
]

export function FeatureTimelineChart({
  data,
  releaseBands = [],
  granularity = 'day',
  launchDate,
  maxFeatures = 5,
}: FeatureTimelineChartProps) {
  const [selectedFeatures, setSelectedFeatures] = useState<string[] | null>(null)

  const periods = useMemo(
    () =>
      [...new Set(data.map((point) => point.period))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [data]
  )

  const featureTotals = useMemo(
    () =>
      data.reduce<Record<string, number>>((acc, point) => {
        acc[point.feature] = (acc[point.feature] || 0) + point.count
        return acc
      }, {}),
    [data]
  )

  const rankedFeatures = useMemo(
    () => Object.entries(featureTotals).sort((a, b) => b[1] - a[1]),
    [featureTotals]
  )

  const allFeatures = useMemo(
    () => rankedFeatures.map(([feature]) => feature),
    [rankedFeatures]
  )

  const topFeatures = useMemo(
    () => allFeatures.slice(0, maxFeatures),
    [allFeatures, maxFeatures]
  )

  const visibleFeatures = (selectedFeatures ?? topFeatures).filter((feature) =>
    featureTotals[feature]
  )

  const periodFeatureCounts = useMemo(
    () =>
      data.reduce<Record<string, Record<string, number>>>((acc, point) => {
        if (!acc[point.period]) {
          acc[point.period] = {}
        }
        acc[point.period][point.feature] =
          (acc[point.period][point.feature] || 0) + point.count
        return acc
      }, {}),
    [data]
  )

  const toggleFeature = (feature: string) => {
    setSelectedFeatures((current) => {
      const active = current ?? topFeatures
      if (active.includes(feature)) {
        return active.filter((item) => item !== feature)
      }
      return [...active, feature]
    })
  }

  const datasets: ChartDataset<'line', number[]>[] = visibleFeatures.map(
    (feature, index) => ({
      label: feature,
      data: periods.map((period) => periodFeatureCounts[period]?.[feature] || 0),
      borderColor: COLORS[index % COLORS.length],
      backgroundColor: COLORS[index % COLORS.length],
      tension: 0.3,
    })
  )

  const labels = useMemo(
    () =>
      periods.map((period) => {
        const date = new Date(period)
        if (Number.isNaN(date.getTime())) {
          return period
        }

        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          ...(granularity === 'week' ? { year: '2-digit' as const } : {}),
        })
      }),
    [granularity, periods]
  )

  const launchDateText = (() => {
    if (!launchDate) {
      return null
    }
    const parsed = new Date(launchDate)
    if (Number.isNaN(parsed.getTime())) {
      return `Launch marker: ${launchDate}`
    }
    return `Launch marker: ${parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`
  })()

  const chartData = {
    labels,
    datasets,
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => {
            const period = periods[items[0]?.dataIndex ?? 0]
            const date = new Date(period)
            if (Number.isNaN(date.getTime())) {
              return period
            }
            return date.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          },
          label: (context: TooltipItem<'line'>) =>
            `${context.dataset.label}: ${Number(context.parsed.y).toLocaleString()} events`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'hsl(214.3, 31.8%, 91.4%)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  }

  return (
    <div className="space-y-3">
      {launchDateText && (
        <p className="text-xs text-muted-foreground">{launchDateText}</p>
      )}
      <ReleaseBandsLegend releaseBands={releaseBands} />
      <div className="flex flex-wrap items-center gap-2">
        {allFeatures.map((feature) => {
          const isSelected = visibleFeatures.includes(feature)
          return (
            <label
              key={feature}
              className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleFeature(feature)}
                className="h-3.5 w-3.5"
              />
              <span>{feature}</span>
              <span className="text-muted-foreground">
                {featureTotals[feature].toLocaleString()}
              </span>
            </label>
          )
        })}
        <button
          type="button"
          onClick={() => setSelectedFeatures(null)}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-secondary"
        >
          Top {Math.min(maxFeatures, allFeatures.length)}
        </button>
        <button
          type="button"
          onClick={() => setSelectedFeatures(allFeatures)}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-secondary"
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setSelectedFeatures([])}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-secondary"
        >
          Clear
        </button>
      </div>
      <div className="h-[320px]">
        {visibleFeatures.length ? (
          <Line
            data={chartData}
            options={options}
            plugins={[createReleaseBandsPlugin(periods, releaseBands)]}
          />
        ) : (
          <div className="flex h-full items-center justify-center rounded-md border border-dashed border-border text-sm text-muted-foreground">
            Select one or more features.
          </div>
        )}
      </div>
    </div>
  )
}
